"use client"

import { useRef, useEffect, useState } from "react"
import type { Hero } from "@/types/hero.types"
import type { MerchantConfig } from "@/data/merchant-types"
import type { HeroInventory } from "@/types/item.types"

import { PortraitSection } from "./game-ui/bottom-bar/portrait-section"
import { ShopkeepPortraitSection } from "./game-ui/bottom-bar/shopkeep-portrait-section"
import { ShopHeroName } from "./game-ui/bottom-bar/shop-hero-name"
import { ShopMerchantName } from "./game-ui/bottom-bar/shop-merchant-name"
import { HealthBar } from "./game-ui/bottom-bar/health-bar"
import { ManaBar } from "./game-ui/bottom-bar/mana-bar"
import { XpBar } from "./game-ui/bottom-bar/xp-bar"
import { DamageBar } from "./game-ui/bottom-bar/damage-bar"
import { ArmorBar } from "./game-ui/bottom-bar/armor-bar"
import { SkillsSection } from "./game-ui/bottom-bar/skills-section"
import { InventorySection } from "./game-ui/bottom-bar/inventory-section"

// Background image dimensions
const BG_WIDTH = 512
const BG_HEIGHT = 152

interface ShopBottomBarProps {
  heroTypes: typeof import("@/data/hero-types").heroTypes
  selectedHeroType: string
  hero: Hero | null
  heroInventory?: HeroInventory
  onSelectHero?: () => void
  isDesktop?: boolean
  merchant: MerchantConfig | null // Add merchant prop
  shopRate?: number // Shop sell rate (0.35, 0.5, or 0.65)
  onSellItem?: (slotIndex: number) => void
}

export const ShopBottomBar = ({
  heroTypes,
  selectedHeroType,
  hero,
  heroInventory,
  onSelectHero,
  isDesktop = false,
  merchant, // Receive merchant prop
  shopRate,
  onSellItem,
}: ShopBottomBarProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [barWidth, setBarWidth] = useState(0)

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setBarWidth(containerRef.current.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener("resize", updateWidth)
    return () => window.removeEventListener("resize", updateWidth)
  }, [])

  // Calculate scale based on container width relative to background image
  const scale = barWidth > 0 ? barWidth / BG_WIDTH : 1
  const bgHeight = BG_HEIGHT * scale

  // Get hero config for type name - fallback to selectedHeroType when hero is not available
  const heroConfig = hero
    ? heroTypes[hero.type]
    : selectedHeroType
      ? heroTypes[selectedHeroType]
      : null
  const heroDamage = heroConfig?.baseStats.damage || { min: 0, max: 0 }
  const heroArmor = heroConfig?.baseStats.armor ?? 0

  return (
    <div
      ref={containerRef}
      id="shop-bottom-bar"
      className={`${isDesktop ? "absolute" : "fixed"} bottom-0 left-0 right-0 z-40 flex-shrink-0`}
      style={{
        height: bgHeight || BG_HEIGHT,
        paddingBottom: isDesktop ? "0" : "env(safe-area-inset-bottom)",
      }}
    >
      <div className="absolute inset-0">
        <img
          src="/images/bottom-bar-shop-bg.png"
          alt="Shop bottom bar background"
          width={BG_WIDTH}
          height={BG_HEIGHT}
          className="w-full h-full"
          style={{ imageRendering: "pixelated" }}
        />
      </div>

      {/* All UI elements positioned relative to background */}
      <PortraitSection
        heroConfig={heroConfig}
        heroType={hero?.type}
        heroColor={hero?.color}
        selectedHeroType={selectedHeroType}
        onSelectHero={onSelectHero}
        scale={scale}
        variant="shop"
      />

      <ShopkeepPortraitSection scale={scale} merchant={merchant} />

      <ShopHeroName name={hero?.name || heroConfig?.displayName || heroConfig?.name || "Hero"} scale={scale} />

      <ShopMerchantName scale={scale} />

      <HealthBar current={hero?.health || 0} max={hero?.maxHealth || 1} scale={scale} variant="shop" />

      <ManaBar current={hero?.mana || 0} max={hero?.maxMana || 1} scale={scale} variant="shop" />

      <XpBar
        current={hero?.experience || 0}
        toNextLevel={hero?.experienceToNextLevel || 1}
        level={hero?.level || 1}
        heroType={hero?.type || ""}
        scale={scale}
        variant="shop"
      />

      <DamageBar min={heroDamage.min} max={heroDamage.max} scale={scale} variant="shop" />

      <ArmorBar armor={heroArmor} scale={scale} variant="shop" />

      <SkillsSection scale={scale} hero={hero} heroConfig={heroConfig} variant="shop" />

      <InventorySection scale={scale} inventory={heroInventory} variant="shop" shopRate={shopRate} onSellItem={onSellItem} />
    </div>
  )
}
